export type ContractStageKey = 'WRITING' | 'CUSTOM' | 'DONE' | string;

/** 단계 탭에 임베드할 화면 종류 */
export type ContractStageLinkType =
  | 'BOARD'
  | 'PRODUCT_LIST'
  | 'PRODUCT_ORDER'
  | 'PRODUCT_ACCEPTANCE'
  | 'PRODUCT_MID'
  | 'PRODUCT_FINAL'
  | 'PRODUCT_PROCESS_CHART'
  | 'PRODUCT_MANUAL'
  | 'PRODUCT_DRAWING'
  | 'PRODUCT_DAILY'
  | 'PRODUCT_NCR'
  | 'QUALITY_SUGGESTION'
  | 'EXTERNAL_AUDIT'
  | 'OFFICIAL_DOCUMENT'
  | 'INTERNAL_DRAFT'
  | 'MEETING_MINUTES'
  | 'SAFETY_PPE'
  | 'SAFETY_CHECK'
  | string;

export interface ContractStage {
  stageId?: number | null;
  stageName: string;
  stageKey: ContractStageKey;
  sortOrder?: number | null;
  boardId?: number | null;
  boardName?: string | null;
  /** BOARD | PRODUCT_* | QUALITY_* | OFFICIAL_DOCUMENT | INTERNAL_DRAFT */
  linkType?: ContractStageLinkType | null;
}

export interface ContractItem {
  contractItemId?: number | null;
  productId?: number | null;
  productName: string;
  unit?: string;
  quantity: number;
  unitPrice?: number | null;
  amount?: number;
  sortOrder?: number | null;
}

export interface Contract {
  contractId: number;
  sortOrder: number | null;
  documentNo?: string | null;
  contractNo?: string | null;
  contractName: string;
  clientName?: string | null;
  clientManagerName?: string | null;
  clientManagerPhone?: string | null;
  startDate?: string | null;
  endDate?: string | null;
  deliveryDueDate?: string | null;
  managerName?: string | null;
  deliveryPlace?: string | null;
  deliveryManagerName?: string | null;
  deliveryManagerPhone?: string | null;
  currentStage?: string | null;
  currentStageId?: number | null;
  stages?: ContractStage[];
  urgent?: boolean | null;
  draft?: boolean | null;
  deletedAt?: string | null;
  purgeAt?: string | null;
  createdUserId?: number | null;
  createdUserName?: string | null;
  lastUpdatedUserId?: number | null;
  lastUpdatedUserName?: string | null;
  deletedUserId?: number | null;
  deletedUserName?: string | null;
  canEdit?: boolean | null;
  canDelete?: boolean | null;
  canRestore?: boolean | null;
  memo?: string | null;
  totalAmount?: number | null;
  itemCount?: number | null;
  items?: ContractItem[];
  createdAt?: string | null;
  updatedAt?: string | null;
}

export interface ContractPayload {
  /** 실제 계약번호 (선택) */
  contractNo?: string;
  contractName: string;
  clientName?: string;
  clientManagerName?: string;
  clientManagerPhone?: string;
  startDate?: string;
  endDate?: string;
  deliveryDueDate?: string;
  managerName?: string;
  deliveryPlace?: string;
  deliveryManagerName?: string;
  deliveryManagerPhone?: string;
  currentStage?: string;
  currentStageId?: number | null;
  stages?: ContractStage[];
  urgent?: boolean;
  draft?: boolean;
  memo?: string;
  items?: ContractItem[];
}

/** @deprecated 구 단계 필터용. 신규는 stages 기반 */
export const CONTRACT_STAGES = ['작성', '인수검사', '중간검사', '제품검사', '대기', '납품', '완료'] as const;

export const DEFAULT_MIDDLE_STAGE_NAMES = [
  '발주서',
  '인수검사',
  '중간검사',
  '제품검사',
  '대기',
  '납품',
] as const;

const DEFAULT_MIDDLE_STAGE_LINKS: Record<string, ContractStageLinkType> = {
  발주서: 'PRODUCT_ORDER',
  인수검사: 'PRODUCT_ACCEPTANCE',
  중간검사: 'PRODUCT_MID',
  제품검사: 'PRODUCT_FINAL',
};

export const CONTRACT_UNITS = ['EA', 'SET', 'BOX', 'KG', 'M', '식'] as const;

export function createDefaultContractStages(): ContractStage[] {
  const middle = DEFAULT_MIDDLE_STAGE_NAMES.map((stageName, index) => ({
    stageName,
    stageKey: 'CUSTOM' as const,
    sortOrder: index + 1,
    boardId: null,
    linkType: DEFAULT_MIDDLE_STAGE_LINKS[stageName] ?? null,
  }));
  return [
    { stageName: '작성', stageKey: 'WRITING', sortOrder: 0, boardId: null, linkType: null },
    ...middle,
    {
      stageName: '완료',
      stageKey: 'DONE',
      sortOrder: middle.length + 1,
      boardId: null,
      linkType: null,
    },
  ];
}

export const STAGE_PRODUCT_LINK_OPTIONS: {
  linkType: ContractStageLinkType;
  label: string;
}[] = [
  { linkType: 'PRODUCT_LIST', label: '제품목록' },
  { linkType: 'PRODUCT_ORDER', label: '발주서' },
  { linkType: 'PRODUCT_ACCEPTANCE', label: '인수검사' },
  { linkType: 'PRODUCT_MID', label: '중간검사' },
  { linkType: 'PRODUCT_FINAL', label: '제품검사' },
  { linkType: 'PRODUCT_PROCESS_CHART', label: '공정도' },
  { linkType: 'PRODUCT_MANUAL', label: '사용설명서' },
  { linkType: 'PRODUCT_DRAWING', label: '도면 관리' },
  { linkType: 'PRODUCT_DAILY', label: '생산일보' },
  { linkType: 'PRODUCT_NCR', label: '부적합품 보고서' },
];

export const STAGE_QUALITY_LINK_OPTIONS: {
  linkType: ContractStageLinkType;
  label: string;
}[] = [
  { linkType: 'QUALITY_SUGGESTION', label: '제안 신청서' },
  { linkType: 'EXTERNAL_AUDIT', label: '외부심사' },
];

export const STAGE_DOC_LINK_OPTIONS: {
  linkType: ContractStageLinkType;
  label: string;
}[] = [
  { linkType: 'OFFICIAL_DOCUMENT', label: '공문서' },
  { linkType: 'INTERNAL_DRAFT', label: '내부 기안서' },
  { linkType: 'MEETING_MINUTES', label: '회의록' },
];

export const STAGE_SAFETY_LINK_OPTIONS: {
  linkType: ContractStageLinkType;
  label: string;
}[] = [
  { linkType: 'SAFETY_PPE', label: '안전장비 지급현황' },
  { linkType: 'SAFETY_CHECK', label: '안전관리 점검시트' },
];

const EMBED_LINK_TYPES = new Set<string>([
  ...STAGE_PRODUCT_LINK_OPTIONS.map((o) => o.linkType),
  ...STAGE_QUALITY_LINK_OPTIONS.map((o) => o.linkType),
  ...STAGE_DOC_LINK_OPTIONS.map((o) => o.linkType),
  ...STAGE_SAFETY_LINK_OPTIONS.map((o) => o.linkType),
]);

/** @deprecated use isEmbedStageLink — 제품·품질·문서 화면 연동 포함 */
export function isProductStageLink(linkType?: string | null) {
  return isEmbedStageLink(linkType);
}

export function isEmbedStageLink(linkType?: string | null) {
  return !!linkType && EMBED_LINK_TYPES.has(linkType);
}

export function createEmptyContractItem(): ContractItem {
  return { productName: '', unit: 'EA', quantity: 1, unitPrice: null, productId: null };
}

export function calcItemAmount(item: Pick<ContractItem, 'quantity' | 'unitPrice'>) {
  const qty = Number(item.quantity) > 0 ? Number(item.quantity) : 0;
  const price = item.unitPrice == null ? 0 : Math.max(0, Number(item.unitPrice) || 0);
  return qty * price;
}

export function isWritingStage(stage?: ContractStage | null) {
  return stage?.stageKey === 'WRITING' || stage?.stageName === '작성';
}
